import { IconEye } from "@tabler/icons-react";
import type { FileModel, TitleGetModel } from "api";
import { waybillTitleTypesEnum } from "helpers/enums/waybillTitleTypes";
import type { getWaybillTitlePrices } from "helpers/waybillTitlePrices";
import React, { useEffect, useState } from "react";
import { getTitle } from "sources/ebills";
import { getTitleFilePrices } from "sources/waybillTitleFiles";

interface TitleFileViewProps {
    waybillId: number;
    title: { id: number; type: string };
}

interface FileViewState {
    loading: boolean;
    file?: FileModel;
    prices?: ReturnType<typeof getWaybillTitlePrices>;
    error?: string;
}

/** File access remains independent of title signing and the CryptoPro plugin. */
export const WaybillTitleFileView = ({ waybillId, title }: TitleFileViewProps) => {
    const [expanded, setExpanded] = useState(false);
    const [state, setState] = useState<FileViewState>({ loading: true });
    const { id: titleId, type } = title;
    const contentId = `waybill-title-file-${waybillId}-${titleId}`;

    useEffect(() => {
        if (!expanded) {
            return;
        }
        let active = true;
        let file: FileModel | undefined;
        getTitle(waybillId, titleId)
            .then(async (data: TitleGetModel | false) => {
                if (!active) {
                    return;
                }
                if (!data || !data.file?.accessUrlLink) {
                    throw new Error("Файл титула недоступен");
                }
                file = data.file;
                if (active) {
                    setState({ loading: type === waybillTitleTypesEnum.T5, file });
                }
                if (type === waybillTitleTypesEnum.T5) {
                    const prices = await getTitleFilePrices(data.file.accessUrlLink);
                    if (active) {
                        setState({ loading: false, file, prices });
                    }
                }
            })
            .catch(() => {
                if (active) {
                    setState({
                        loading: false,
                        file,
                        error: file
                            ? "Не удалось загрузить стоимость перевозки. Скачайте исходный файл титула."
                            : "Не удалось получить файл титула. Попробуйте открыть его ещё раз.",
                    });
                }
            });
        return () => {
            active = false;
        };
    }, [expanded, waybillId, titleId, type]);

    const toggle = () => {
        if (!expanded) {
            setState({ loading: true });
        }
        setExpanded(!expanded);
    };

    return (
        <div className="mt-2">
            <button
                type="button"
                className="btn btn-link btn-xs p-0 d-inline-flex align-items-center text-underline"
                onClick={toggle}
                aria-expanded={expanded}
                aria-controls={contentId}
            >
                <IconEye size={14} className="mr-1" aria-hidden="true" />
                Просмотреть файл {type}
            </button>
            {expanded && (
                <div id={contentId} className="mt-1 font-size-14">
                    {state.file && (
                        <a href={state.file.accessUrlLink} download={state.file.originalFileName}>
                            {state.file.originalFileName || "Скачать исходный файл титула"}
                        </a>
                    )}
                    {state.loading && <div role="status">Загрузка…</div>}
                    {state.error && <div role="alert">{state.error}</div>}
                    {state.prices && (
                        <div>
                            <div>
                                Стоимость перевозки без налога: <b>{state.prices.price}</b>
                            </div>
                            <div>
                                Налоговая ставка: <b>{state.prices.taxRate}</b>
                            </div>
                            <div>
                                Стоимость перевозки с налогом: <b>{state.prices.priceWithTaxRate}</b>
                            </div>
                            <div>
                                Сумма налога, предъявляемая покупателю (грузоотправителю):{" "}
                                <b>{state.prices.taxPrice}</b>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
